<script setup lang="ts">
import { computed, inject } from "vue";
import { useAuthStore, useAwardStore, useCandidatesStore } from "@/stores";
import { format, isBefore, isFuture, isToday, set, isPast } from "date-fns";
import VerifyOwnershipDialog from "@/components/CandidateRegistration/VerifyOwnershipDialog.vue";
import useSWRV from "swrv";
import useCandidate from "@/composables/use-candidate";
import useAlerts from "@/composables/use-alerts";
import useAwards from "@/composables/use-awards";
import GenericDialog from "@/components/GenericDialog.vue";
import { formatPhone } from "@/util/phone";
import Alerts from "@/components/Nav/Alerts.vue";
import { useRouter } from "vue-router";
import { errorToast } from "@/util/toast";

const $dialog: any = inject("$dialog");

const props = defineProps<{ id?: string }>();

const router = useRouter();
const authStore = useAuthStore();
const awardStore = useAwardStore();
const candidatesStore = useCandidatesStore();

const {
  awardYear,
  settings: awardSettings,
  nomineesPublishedDate,
  finalistsPublishedDate,
  awardsCeremonyOver,
} = useAwards();
const { candidate, status: candidateStatus } = useCandidate(props.id);

const { canClaimNomination } = useAlerts();

const loading = computed(() => {
  return !awardSettings.value || !candidate.value;
});

const today = set(new Date(), {
  date: new Date().getDate(),
  hours: 0,
  minutes: 0,
});

const { data: announcementItems } = useSWRV(
  "announcement-items",
  () => awardStore.getAnnouncements(),
  {
    refreshInterval: 60_000 * 5,
    shouldRetryOnError: false,
  },
);

const publishedAnnouncements = computed(() => {
  return (
    announcementItems.value?.filter(
      (a) => isPast(a.date) && (!a.expiryDate || isFuture(a.expiryDate)),
    ) || []
  );
});

const { data: calendarItems } = useSWRV(
  "calendar-items",
  () => awardStore.getCalendarItems(),
  {
    refreshInterval: 60_000 * 5,
    shouldRetryOnError: false,
  },
);

const phones = computed(() => {
  if (!candidate.value) {
    return [];
  }
  const phones: string[] = [];
  const { primaryPhone = "", locations = [] } = candidate.value;
  if (primaryPhone) {
    phones.push(primaryPhone);
  }
  for (const location of locations) {
    if (location.verifiedYears?.length && location.phone) {
      phones.push(location.phone);
    }
  }
  return [...new Set(phones)];
});

const websiteDomain = computed(() => {
  if (!candidate.value?.website) {
    return "";
  }
  try {
    let _website = candidate.value.website;
    if (!_website.startsWith("http")) {
      _website = `http://${_website}`;
    }
    const url = new URL(_website);
    return url.hostname.replace(/^www\./, "");
  } catch (error) {
    return "";
  }
});

const domainsMatch = computed(() => {
  const emailDomainMatch = authStore.user?.email?.match(/^.*@(.*)$/);
  let emailDomain: string = "";
  if (emailDomainMatch) {
    emailDomain = emailDomainMatch[1];
  }
  if (!emailDomain || !websiteDomain.value) {
    return false;
  }
  return (
    emailDomain.toLocaleLowerCase() === websiteDomain.value.toLocaleLowerCase()
  );
});

const candidateAwardStatus = computed(() => {
  if (awardsCeremonyOver.value) {
    return [];
  }
  if (finalistsPublishedDate.value && candidateStatus.value.isFinalist) {
    const results =
      candidate.value?.results.filter(
        (r) => r.awardYear === awardYear.value && r.finalist,
      ) || [];
    return (
      results.map((r) => ({
        categoryName: r.categoryName,
        status: "Finalist",
      })) || []
    );
  }
  if (nomineesPublishedDate.value && candidateStatus.value.isNominee) {
    const categories = candidate.value?.results.filter(
      (r) => r.awardYear === awardYear.value && r.finalized,
    );
    return (
      categories?.map((r) => ({
        categoryName: r.categoryName,
        status: "Nominee",
      })) || []
    );
  }
  return [];
});

function verifyOwnership() {
  if (!candidate.value || !phones.value.length) {
    return;
  }
  $dialog.addDialog({
    component: VerifyOwnershipDialog,
    props: {
      candidate: candidate.value,
      phones: [...new Set(phones.value)],
      email: authStore.user.email,
      domainsMatch: domainsMatch.value,
      formData: {
        email: { value: authStore.user.email },
        phone: { value: formatPhone(candidate.value.primaryPhone) },
        firstName: { value: authStore.user.firstName },
        lastName: { value: authStore.user.lastName },
        title: { value: authStore.user.title },
        domain: "",
      },
      onVerified: ({ manual }: { manual: boolean }) => {
        if (!manual && candidate.value) {
          candidatesStore
            .verifyOwnership(candidate.value.id)
            .then((success) => {
              if (success) {
                authStore.user.candidateVerified = true;
                $dialog.addDialog({
                  component: GenericDialog,
                  props: {
                    title: "Verified!",
                    message:
                      "Thanks for verifying. You now have full access to the Candidate Dashboard.",
                    showCancelButton: false,
                  },
                });
              }
            });
        } else if (manual) {
          $dialog.addDialog({
            component: GenericDialog,
            props: {
              title: "Thanks!",
              message: "We'll be in contact with you soon.",
              showCancelButton: false,
            },
          });
        }
      },
    },
  });
}

function claimNomination() {
  if (authStore.masquerading) {
    return errorToast("As an admin, you cannot claim nominations.", {
      timeout: 5000,
    });
  }
  router.push({ name: "claim-nomination" });
}
</script>

<template>
  <div class="home">
    <LoadingOverlay :is-loading="loading" />
    <div class="max-w-[56rem] w-full flex flex-col gap-2" v-if="!loading">
      <Card class="max-w-[56rem] justify-between hidden md:flex"
        ><h2>Home</h2>
        <Alerts />
      </Card>
      <div class="flex flex-col lg:grid lg:grid-cols-2 gap-2">
        <div class="">
          <Card class="flex h-full w-full flex-col justify-between gap-y-2">
            <div>
              <h2>Welcome!</h2>
              <p v-html="awardStore.welcomeText ?? ''" />
            </div>
            <div v-if="canClaimNomination" class="flex justify-center">
              <Button @click="() => claimNomination()"
                >Claim Your Nomination!</Button
              >
            </div>
            <div v-else-if="!authStore.user.candidateVerified">
              <div v-if="phones.length" class="flex flex-col gap-y-2">
                <p
                  class="rounded-sm border border-yellow-300 bg-yellow-100 p-2 font-medium"
                >
                  To gain full access to the Candidate Dashboard, please verify
                  that you own or represent
                  <span class="italic">{{ candidate?.name }}.</span>
                </p>
                <div class="flex justify-center">
                  <Button class="w-40" @click="verifyOwnership"
                    >Verify Ownership</Button
                  >
                </div>
              </div>
              <div
                v-else
                title="Review in Progress"
                class="flex h-[12rem] w-full max-w-md flex-col gap-y-2"
              >
                <p
                  class="rounded-sm border border-yellow-300 bg-yellow-100 p-2 font-medium"
                >
                  Once we have reviewed and accepted your registration, you will
                  have full access to the Candidate Dashboard.
                </p>
              </div>
            </div>
          </Card>
        </div>
        <div
          :class="{ 'row-span-2': !candidateAwardStatus }"
          class="flex flex-col gap-2 row-span-2"
        >
          <Card class="w-full h-full" v-if="calendarItems?.length">
            <h2>Calendar</h2>
            <ul>
              <li
                v-for="(item, index) in calendarItems"
                :key="index"
                class="py-2"
              >
                <div
                  :class="{
                    italic: isToday(item.date),
                    'text-green-600': isToday(item.date),
                    'text-gray-400': isBefore(item.date, today),
                  }"
                >
                  <p class="font-bold">
                    {{ format(item.date, "MMM d, yyyy") }}
                  </p>
                  <p v-html="item.text"></p>
                </div>
              </li>
            </ul>
          </Card>
          <Card
            :title="`Your ${awardYear} Status`"
            v-if="candidateAwardStatus.length"
            class="lg:h-[24rem]"
          >
            <table class="table-fixed border-l border-r border-t p-4 w-full">
              <thead>
                <tr>
                  <th class="border px-4 py-2">Category</th>
                  <th class="border px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(category, index) in candidateAwardStatus"
                  :key="`${index}${category.categoryName}`"
                  :class="{ 'bg-gray-200': index % 2 === 1 }"
                >
                  <td class="border px-2 py-1 text-center">
                    Best {{ category.categoryName }}
                  </td>
                  <td class="border px-2 py-1 text-center">
                    {{ category.status }}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>

        <div class="">
          <Card
            class="w-full flex flex-col grow h-full"
            v-if="publishedAnnouncements.length"
          >
            <h2>Announcements</h2>
            <ul>
              <li
                v-for="(item, index) in publishedAnnouncements"
                :key="index"
                class="py-2"
              >
                <p class="font-bold">
                  {{ format(item.date, "MMM d, yyyy") }}
                </p>
                <p v-html="item.text"></p>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>
